/**
 * Created by Ning Jiang on 11/23/2016.
 */

import {IGameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/IGameConfig";
import {BetWinMode} from "@nolimitcity/slot-game/bin/core/gametype/BetWinMode";
import {SpinMode} from "@nolimitcity/slot-game/bin/core/gametype/SpinMode";
import {IReelSets} from "@nolimitcity/slot-game/bin/core/reel/reelset/IReelSets";
import {ReelSymbolName} from "@nolimitcity/slot-game/bin/core/reelsymbol/ReelSymbolName";
import {ReelSymbolStateCreator} from "@nolimitcity/slot-game/bin/core/reelsymbol/ReelSymbolStateCreator";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {LayerScaleOption} from "@nolimitcity/slot-game/bin/core/stage/LayerScaleOption";
import {IDeviceDependentConfig} from "@nolimitcity/slot-game/bin/core/useragent/UserAgent";
import {IWinRatios, WinRatioType} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {Back, Ease} from "gsap";
import {IWixxSymbolStates} from "./reelsymbol/WixxReelSymbol";
import {IWixxLayersConfig} from "./stage/IWixxLayersConfig";
import {WixxHelper} from "./utils/WixxHelper";

export class WixxGameConfig implements IGameConfig {

    public static instance:WixxGameConfig = new WixxGameConfig();

    BROWSER_FPS:number = 30;

    GAME_WIDTH:number = 1280;
    GAME_HEIGHT:number = 720;

    DEFAULT_LAYER_SCALE_OPTION:IDeviceDependentConfig<LayerScaleOption> = {
        desktop : {landscape : LayerScaleOption.FIT_WINDOW},
        mobile : {
            landscape : LayerScaleOption.FIT_WINDOW,
            portrait : LayerScaleOption.REELS_FIT_STAGE
        }
    };
    LAYERS:IWixxLayersConfig = {
        BACKGROUND : {name : "background"},
        LOGO_ON_BACKGROUND : {name : "logoOnBackground"},
        REEL_BACKGROUND : {name : "reelBackground"},
        SYMBOLS : {name : "symbols"},
        STACKED_REEL_EFFECTS : {name : "stackedReelEffects"},
        LANDSCAPE_WIN_WAYS : {name : "landscapeWinWays"},
        REEL_ATTENTION : {name : "reelAttention"},
        WIN_PRESENTATIONS : {name : "winPresentations"},
        FOREGROUND_RESPIN : {name : "foregroundRespin"},
        BIG_WIN : {name : "bigWin"},
        PORTRAIT_SPECIFIC : {name : "portraitSpecific"},
        PORTRAIT_WIN_WAYS : {name : "portraitWinWays"},
        WIN_FIELD : {name : "winField"},
        INTRO : {name : "introScreen", scaleOption : LayerScaleOption.FIT_WINDOW},
        TWEAK_MODULE : {name : "tweakModule", scaleOption : LayerScaleOption.FIT_STAGE}
    };

    BET_WIN_MODE:BetWinMode = BetWinMode.BET_WAY;
    SPIN_MODE:SpinMode = SpinMode.SPIN;

    REEL_AREA_POS_X:number = 200;
    REEL_AREA_POS_Y:number = 24;
    REEL_AREA_WIDTH:number = 880;
    REEL_AREA_HEIGHT:number = 564;
    REEL_AREA_MASK_RECT:PIXI.Rectangle = new PIXI.Rectangle(72, 0, 1136, 612);

    REELS_NUM:number = 4;
    REEL_DISPLAY_POSITIONS:[number, number][] = [[110, 0], [330, 0], [550, 0], [770, 0]];
    SYMBOLS_NUM_IN_REEL:[number, number, number, number, number] = [0, 1, 3, 1, 0];

    SYMBOL_STATES:IWixxSymbolStates = {
        normal : {
            prio : 0,
            keyword : "normal",
            texturesCreator : (symName:string, stateKey:string) => {
                if(WixxHelper.isBoostedSymbol(symName)) {
                    return [GameResources.getTextures(symName).pop()!];
                } else {
                    return GameResources.getTextures(`${symName}_idle`).slice(0, 1);
                }
            }
        },
        idle : {
            prio : 1,
            keyword : "idle",
            texturesCreator : (symName:string, stateKey:string) => {
                if(WixxHelper.isBoostedSymbol(symName)) {
                    return GameResources.getTextures(symName);
                }

                /* if(UserAgent.isMobile) {
                     return GameResources.getTextures(`${symName}_idle`);
                 }*****/

                return GameResources.getTextures(`${symName}_idle`).slice(1);
            }
        },
        spin : {
            prio : 2,
            keyword : "spin"
        },
        attention : {
            prio : 3,
            keyword : "attention",
            texturesCreator : (symName:string, stateKey:string) => {
                if(WixxHelper.isBoostedSymbol(symName)) {
                    return [GameResources.getTextures(symName).pop()!];
                } else {
                    return GameResources.getTextures(`${symName}_idle`).slice(0, 1);
                }
            },
            symbols : ["W", "M1_boosted"]
        },
        noWin : {
            prio : 4,
            keyword : "noWin",
            texturesCreator : (symName:string, stateKey:string) => {
                if(WixxHelper.isBoostedSymbol(symName)) {
                    return [GameResources.getTextures(symName).pop()!];
                } else {
                    return GameResources.getTextures(`${symName}_idle`).slice(0, 1);
                }
            }
        },
        hidden : {
            prio : 5,
            keyword : "hidden",
        },
        win : {
            prio : 6,
            keyword : "win",
            texturesCreator : (symName:string, stateKey:string) => {
                if(symName === ReelSymbolName.WILD) {
                    return GameResources.getTextures("W_win");
                }
                if(WixxHelper.isBoostedSymbol(symName)) {
                    return GameResources.getTextures(symName);
                }

                return ReelSymbolStateCreator.defaultSymbolStateTexturesCreator(symName, stateKey);
            }
        },
        boost : {
            prio : 7,
            keyword : "boost",
            texturesCreator : (symName:string, stateKey:string) => {
                return GameResources.getTextures(`${symName}_boosted`);
            },
            symbols : ["M1", "M2", "M3"]
        }
    };
    SYMBOL_WIDTH:number = 220;
    SYMBOL_HEIGHT:number = 188;
    SYMBOL_BOTTOM_PADDING:number = 0;

    REEL_SPIN_TIME:number = 0.8;
    REEL_FAST_SPIN_SPIN_TIME:number = 0.2;
    MIN_STOPPING_TIME:number = 2;
    REEL_SPIN_START_SYMBOL_CHANGE_TO_SPIN_STATE_DURATION:number = 0.15;
    REEL_SPIN_START_SYMBOL_CHANGE_TO_SPIN_STATE_DELAY:number = 0.25;
    REEL_SPIN_START_EASE:Ease = Back.easeIn.config(2);
    REEL_SPIN_STOP_SYMBOL_CHANGE_FROM_SPIN_STATE_DURATION:number = 0.1;
    REEL_SPIN_STOP_SYMBOL_CHANGE_FROM_SPIN_STATE_DELAY:number = 0;
    REEL_SPIN_STOP_ELASTIC_CONFIG:[number, number] = [1.5, 1];
    REEL_SPIN_QUICK_STOP_ELASTIC_CONFIG:[number, number] = [0.5, 0.5];

    REEL_SPIN_START_SPEED:number = 0.3;
    REEL_SPIN_SPIN_SPEED:number = 0.05;
    REEL_NEAR_WIN_SPIN_SPEED:number = 0.03;
    REEL_SPIN_STOP_SPEED:number = 0.2;
    REEL_SPIN_QUICK_STOP_SPEED:number = 0.11;

    // the delay from the previous reel totally stop to this reel totally stop.
    REEL_STOP_DELAY:number[] = [0, 0.4, 0.4, 0.4];
    REEL_QUICK_STOP_DELAY:number[] = [0, 0.1, 0.1, 0.1];
    REEL_NEAR_WIN_STOP_DELAY:number = 3;

    REEL_SETS:IReelSets = {
        "BASE_REELSET" : [
            ["M1", "L4", "L4", "L4", "M3", "L1", "L1", "L1", "M2", "L2", "L2", "L2", "M3", "L3", "L3", "L3", "M2", "L2", "L2", "L2", "M3", "L4", "L4", "L4", "M1", "L2", "L2", "L2", "M2", "M1", "L4", "L4", "L4", "M3", "L1", "L1", "L1", "M2", "L2", "L2", "L2", "M3", "M1", "L3", "L3", "L3", "M2", "L2", "L2", "L2", "M3", "M2", "L4", "L4", "L4", "M1", "L2", "L2", "L2"],
            ["M3", "L4", "L4", "L4", "L2", "L2", "L2", "M1", "L1", "L1", "L1", "W", "L3", "L3", "L3", "M2", "L1", "L1", "L1", "M3", "L4", "L4", "L4", "L2", "L2", "L2", "M1", "L1", "L1", "L1", "L3", "L3", "L3", "M2", "L1", "L1", "L1", "M3", "M1", "L4", "L4", "L4", "L2", "L2", "L2", "M1", "M2", "L1", "L1", "L1", "L3", "L3", "L3", "M2", "M3", "L1", "L1", "L1", "M3", "L4", "L4", "L4", "L2", "L2", "L2", "M1", "L1", "L1", "L1", "L3", "L3", "L3", "M2", "L1", "L1", "L1", "M3", "W", "L4", "L4", "L4", "L2", "L2", "L2", "M1", "L1", "L1", "L1", "L3", "L3", "L3", "M2", "L1", "L1", "L1", "M3", "M1", "L4", "L4", "L4", "L2", "L2", "L2", "M1", "M2", "L1", "L1", "L1", "L3", "L3", "L3", "M2", "M3", "L1", "L1", "L1", "M3", "L4", "L4", "L4", "L2", "L2", "L2", "M1", "L1", "L1", "L1", "L3", "L3", "L3", "M2", "W", "L1", "L1", "L1", "M3", "L4", "L4", "L4", "L2", "L2", "L2", "M1", "L1", "L1", "L1", "L3", "L3", "L3", "M2", "L1", "L1", "L1", "M3", "M1", "L4", "L4", "L4", "L2", "L2", "L2", "M1", "M2", "L1", "L1", "L1", "L3", "L3", "L3", "M2", "M3", "L1", "L1", "L1", "M3", "L4", "L4", "L4", "L2", "L2", "L2", "W", "M1", "L1", "L1", "L1", "L3", "L3", "L3", "M2", "L1", "L1", "L1", "M3", "L4", "L4", "L4", "L2", "L2", "L2", "M1", "L1", "L1", "L1", "L3", "L3", "L3", "M2", "L1", "L1", "L1", "M3", "M1", "L4", "L4", "L4", "L2", "L2", "L2", "M1", "M2", "L1", "L1", "L1", "L3", "L3", "L3", "M2", "M3", "L1", "L1", "L1", "M3", "L4", "L4", "L4", "L2", "L2", "L2", "M1", "L1", "L1", "L1", "W", "L3", "L3", "L3", "M2", "L1", "L1", "L1", "M3", "L4", "L4", "L4", "L2", "L2", "L2", "M1", "L1", "L1", "L1", "L3", "L3", "L3", "M2", "L1", "L1", "L1", "M3", "M1", "L4", "L4", "L4", "L2", "L2", "L2", "M1", "M2", "L1", "L1", "L1", "L3", "L3", "L3", "M2", "M3", "L1", "L1", "L1"],
            ["M1", "L4", "L4", "L4", "W", "L3", "L3", "L3", "L1", "L1", "L1", "M2", "L2", "L2", "L2", "M3", "L3", "L3", "L3", "M1", "L4", "L4", "L4", "L3", "L3", "L3", "L1", "L1", "L1", "M2", "L2", "L2", "L2", "M3", "L3", "L3", "L3", "M1", "L4", "L4", "L4", "L3", "L3", "L3", "L1", "L1", "L1", "M2", "L2", "L2", "L2", "W", "M3", "L3", "L3", "L3", "M1", "L4", "L4", "L4", "L3", "L3", "L3", "L1", "L1", "L1", "M2", "L2", "L2", "L2", "M3", "L3", "L3", "L3", "M1", "L4", "L4", "L4", "L3", "L3", "L3", "L1", "L1", "L1", "W", "M2", "L2", "L2", "L2", "M3", "L3", "L3", "L3", "M1", "L4", "L4", "L4", "L3", "L3", "L3", "L1", "L1", "L1", "M2", "L2", "L2", "L2", "M3", "L3", "L3", "L3", "M1", "L4", "L4", "L4", "L3", "L3", "L3", "L1", "L1", "L1", "M2", "L2", "L2", "L2", "M3", "L3", "L3", "L3", "M1", "W", "L4", "L4", "L4", "L3", "L3", "L3", "W", "L1", "L1", "L1", "M2", "L2", "L2", "L2", "M3", "L3", "L3", "L3", "L1", "L1", "L1", "M1", "L4", "L4", "L4", "W", "L3", "L3", "L3", "L1", "L1", "L1", "M2", "L2", "L2", "L2", "M3", "L3", "L3", "L3", "M1", "L4", "L4", "L4", "L3", "L3", "L3", "L1", "L1", "L1", "M2", "L2", "L2", "L2", "M3", "L3", "L3", "L3"],
            ["M1", "L3", "L3", "L3", "M3", "L4", "L4", "L4", "M2", "L1", "L1", "L1", "M3", "L4", "L4", "L4", "M3", "L2", "L2", "L2", "M3", "L3", "L3", "L3", "W", "L4", "L4", "L4", "M1", "L3", "L3", "L3", "M3", "M1", "L4", "L4", "L4", "M2", "W", "L1", "L1", "L1", "M3", "M2", "L4", "L4", "L4", "M3", "L2", "L2", "L2", "M2", "M1", "L3", "L3", "L3", "W", "L4", "L4", "L4", "M1", "L3", "L3", "L3", "M3", "W", "L4", "L4", "L4", "M2", "L1", "L1", "L1", "M3", "L4", "L4", "L4", "M3", "L2", "L2", "L2", "M2", "L3", "L3", "L3", "W", "L4", "L4", "L4", "M1", "L3", "L3", "L3", "M3", "M1", "L4", "L4", "L4", "M2", "L1", "L1", "L1", "M3", "M2", "L4", "L4", "L4", "M3", "L2", "L2", "L2", "M2", "M1", "L3", "L3", "L3", "W", "L4", "L4", "L4", "M1", "L3", "L3", "L3", "M3", "L4", "L4", "L4", "M2", "L1", "L1", "L1", "M3", "L4", "L4", "L4", "M3", "L2", "L2", "L2", "M2", "L3", "L3", "L3", "W", "L4", "L4", "L4", "M1", "W", "L3", "L3", "L3", "M3", "M1", "L4", "L4", "L4", "M2", "L1", "L1", "L1", "M3", "M2", "L4", "L4", "L4", "M3", "L2", "L2", "L2", "M2", "M1", "L3", "L3", "L3", "W", "L4", "L4", "L4", "L3", "L3", "L3"]
        ],
        "RED_BOOSTER_REELSET" : [
            ["M2", "L2", "L4", "M1", "M1", "M1", "L3", "L1", "M3", "L2", "L4", "M2", "L1", "L3"],
            ["L4", "M2", "L2", "M1", "M1", "M1", "L1", "M3", "L3", "L2", "L4", "L3", "M3", "L2", "L4", "M2", "L2", "M1", "M1", "M1", "L1", "M3", "L3"],
            ["M2", "L2", "L4", "M1", "M1", "M1", "L3", "L1", "M3", "L2", "L4", "M2", "L1", "L3", "M3", "L4", "M2", "L2", "M1", "M1", "M1", "L1", "M3", "L3"],
            ["L4", "M2", "L2", "M1", "M1", "M1", "L1", "M3", "L3", "L4", "M2", "L2", "M1", "M1", "M1", "L1", "M3", "L3"]
        ],
        "YELLOW_BOOSTER_REELSET" : [
            ["M2", "M2", "M2", "L3", "L1", "L4", "M3", "L4", "M1", "L2", "M3", "L2", "M1", "L3", "L1", "M3", "L4"],
            ["M2", "M2", "M2", "L1", "M1", "L3", "L1", "W", "M3", "L4", "L2", "W", "L3", "M3", "L1", "M1", "L4", "L2", "M2", "M2", "M2", "L2", "L1", "M1", "L3", "W", "M3", "L4", "L3"],
            ["M2", "M2", "M2", "M1", "L3", "L1", "W", "M3", "L4", "L2", "W", "L4", "L3", "L2", "W", "M3", "L4", "M1", "L1", "M2", "M2", "M2", "M1", "L3", "L1", "W", "M3", "L4", "L2"],
            ["M2", "M2", "M2", "L4", "M1", "L3", "L1", "W", "M3", "L2", "W", "L2", "L4", "M1", "L3", "L1", "M3", "L4", "L3", "M3", "M2", "M2", "M2", "L1", "M3", "L4", "L2", "W", "M1", "L3"]
        ],
        "BLUE_BOOSTER_REELSET" : [
            ["M3", "M3", "M3", "L1", "M1", "L3", "M2", "L4", "L1", "L2"],
            ["M3", "M3", "M3", "L4", "L2", "M1", "W", "L3", "L1", "M2", "W", "L3", "L4", "M1", "W", "L1", "M2"],
            ["M3", "M3", "M3", "M1", "L3", "W", "M2", "L4", "L2", "W", "L1", "M1", "L3", "W", "M2", "L4", "L2"],
            ["M3", "M3", "M3", "M3", "M3", "M3", "M3", "M3", "M3", "M3", "M3", "M3"]
        ],
        "RED_YELLOW_BOOSTER_REELSET" : [
            ["M1", "M1", "M1", "M2", "M2", "M2", "M3", "L4", "L1", "M2", "M2", "M2", "M1", "M1", "M1", "L3", "M3", "L2"],
            ["M1", "M1", "M1", "L1", "L2", "M3", "M2", "M2", "M2", "M1", "M1", "M1", "L3", "M3", "L4", "W", "W", "W", "M2", "M2", "M2"],
            ["M1", "M1", "M1", "M2", "M2", "M2", "M3", "L1", "L3", "W", "W", "W", "L4", "L2", "M3", "M1", "M1", "M1", "M2", "M2", "M2"],
            ["M1", "M1", "M1", "M2", "M2", "M2", "W", "W", "W", "M3", "L4", "L3", "M1", "M1", "M1", "M2", "M2", "M2", "L1", "M3", "L2"]
        ],
        "RED_BLUE_BOOSTER_REELSET" : [
            ["M1", "M1", "M1", "M3", "M3", "M3", "M2", "L4", "L2", "M3", "M3", "M3", "L3", "M2", "L1"],
            ["M1", "M1", "M1", "L3", "L4", "M2", "M1", "M1", "M1", "M3", "M3", "M3", "L2", "L1", "M2", "W", "W", "W", "M3", "M3", "M3"],
            ["M1", "M1", "M1", "M2", "L1", "L4", "M3", "M3", "M3", "M1", "M1", "M1", "L2", "M2", "L3", "W", "W", "W", "M3", "M3", "M3"],
            ["W", "W", "W", "W", "W", "W", "W", "W", "W", "W", "W", "W", "W", "W", "W"]
        ],
        "YELLOW_BLUE_BOOSTER_REELSET" : [
            ["M3", "M3", "M3", "L1", "L4", "M1", "M2", "M2", "M2", "M3", "M3", "M3", "L2", "M1", "L3", "M2", "M2", "M2"],
            ["L1", "L4", "M1", "M3", "M3", "M3", "W", "W", "W", "L2", "M1", "L3", "M2", "M2", "M2", "W", "W", "W"],
            ["M3", "M3", "M3", "L3", "L2", "M1", "W", "W", "W", "M2", "M2", "M2", "M1", "L1", "L4", "W", "W", "W"],
            ["W", "W", "W", "W", "W", "W", "W", "W", "W", "W", "W", "W", "W", "W", "W"]
        ]
    };

    WIN_RATIOS:IWinRatios = {
        normalWin : [
            {
                ratio : 0.01,
                text : "Tiny Win",
                duration : 0,
                type : WinRatioType.TINY
            },
            {
                ratio : 1.5,
                text : "Small Win",
                duration : 1.3,
                type : WinRatioType.SMALL
            },
            {
                ratio : 3,
                text : "Medium Win",
                duration : 2.2,
                type : WinRatioType.MEDIUM
            },
            {
                ratio : 5,
                text : "Large Win",
                duration : 3.5,
                type : WinRatioType.LARGE
            }
        ],
        bigWin : [
            {
                ratio : 15,
                text : "Big Win",
                duration : 4.5,
                type : WinRatioType.BIG_WIN
            },
            {
                ratio : 25,
                text : "Mega Win",
                duration : 9.5,
                type : WinRatioType.MEGA_WIN
            },
            {
                ratio : 50,
                text : "Super Mega Win",
                duration : 14,
                type : WinRatioType.SUPER_MEGA_WIN
            },
            {
                ratio : 100,
                text : "Super Mega Win",
                duration : 14,
                type : WinRatioType.SUPER_MEGA_WIN
            }
        ]
    };

    readonly SYMBOL_MULTIPLIER_NO_WIN_ALPHA:number = 0.3;

    readonly GAME_FEATURE_CHANGE_ANIMATION_DURATION:number = 1;
    PERFORMANCE_LOGGER:boolean = true;

    // GAME_TWEAKER_CONFIG:IGameTweakerConfig = {
    //     layer : this.LAYERS.TWEAK_MODULE.name,
    //     baseWindowWidth : 400,
    //     moduleHeight : 32,
    //     copyAllButtonText : "Copy All",
    //     buttonSkinIdle : "button",
    //     buttonSkinOver : "button",
    //     buttonSkinDown : "button",
    //     buttonSkinDisabled : "button-disabled"
    // };


    private constructor() {
        if(WixxGameConfig.instance) {
            throw new Error("WixxGameConfig.constructor() - Instantiation failed: Singleton.");
        }
    }
}